import { RampFormValidator } from '~domain/entities/RampFormValidator';
import {
    IBirthYearValidatorParams,
    IJsonLogicValidatorParams,
    IValidatorParams
} from '~types/validator';
import {
    validateDayFieldValue,
    validateMonthFieldValue,
    validateYearFieldValue
} from '~utils/Date';
import { validateMobile } from '~utils/phone-number-validator';

import * as jsonLogic from 'json-logic-js';
import { mixed } from 'yup';
import { RampFormStore } from './RampFormStore';

export type IUserFormValues = Record<string, string | undefined>;

export class UserFormStore extends RampFormStore<IUserFormValues> {
    constructor(initialValues: IUserFormValues = {}) {
        super(initialValues);
        const formValidator = this.formValidator as RampFormValidator;
        formValidator.registerValidation('mobile', validateMobile.bind(this));
        formValidator.registerValidation('jsonLogic', this.validateWithJsonLogic);
        formValidator.registerValidation('birthYear', this.validateBirthYear);
        formValidator.registerValidation('birthMonth', this.validateBirthMonth);
        formValidator.registerValidation('birthDay', this.validateBirthDay);
    }

    // Validator could be "jsonLogic" or "jsonLogic:xxx", the latter one exists to support having multiple jsonLogic validations.
    // The param after ":" will be the 2nd positional parameter for the following function, which is not in use
    public validateWithJsonLogic = ({ rules, errorMessage }: IJsonLogicValidatorParams) => {
        return mixed().test('jsonLogic', errorMessage, () => {
            return jsonLogic.apply(rules, this.values);
        });
    };

    public validateBirthYear = ({ offsets = [], errorMessage = '' }: IBirthYearValidatorParams) => {
        return mixed().test('birthYear', errorMessage, (year: string) => {
            if (!year) {
                return true;
            } else if (!validateYearFieldValue(year)) {
                return false;
            }
            const birthYear = Number(year);
            if (offsets.length) {
                const [start = 0, end = 0] = offsets;
                const offset = birthYear - new Date().getFullYear();

                return offset <= start && offset >= end;
            }

            return true;
        });
    };

    public validateBirthDay = ({ errorMessage = '' }: IValidatorParams) => {
        return mixed().test('birthDay', errorMessage, (dayValue: string) => {
            const { birthMonth, birthYear } = this.values;

            return validateDayFieldValue(dayValue, birthYear, birthMonth);
        });
    };

    public validateBirthMonth = ({ errorMessage = '' }: IValidatorParams) => {
        return mixed().test('birthMonth', errorMessage, (monthValue: string) => {
            return validateMonthFieldValue(monthValue);
        });
    };
}
